import { ApolloClient, InMemoryCache, ApolloLink, NetworkStatus } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { makeLogout } from 'src/utils/auth';
import { getAccessToken } from 'src/utils/tokenService';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { captureException } from '@sentry/react';

const uploadLink = createUploadLink({
  uri: `${process.env.BASE_URL}api/public/api/graphql`,
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      captureException(new Error(`GraphQL Error: ${message}`), {
        tags: {
          context: 'graphql_error',
        },
        extra: {
          path: JSON.stringify(path, null, 2),
          locations: JSON.stringify(locations, null, 2),
          extensions: JSON.stringify(extensions, null, 2),
        },
      });

      if (message === 'Unauthenticated.') {
        makeLogout({ noEvents: true });
      }
    });
  }
  if (networkError) {
    captureException(networkError, {
      tags: {
        context: 'network_error',
      },
      extra: {
        networkError: JSON.stringify(networkError, null, 2),
      },
    });
  }
});

const authLink = setContext(async (operation, { headers }) => {
  const token = await getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  };
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, uploadLink]),
  cache: new InMemoryCache(),

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'no-cache',
    },
    query: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-first',
    },
  },
});

type IsQueryLoading = (options: { loading: boolean; data: any; networkStatus: NetworkStatus }) => boolean;

export const isQueryLoading: IsQueryLoading = ({ loading, data, networkStatus }) => {
  return (loading && !data) || networkStatus === NetworkStatus.refetch;
};
